import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import Modal from './Modal';
import InfoButton from './InfoButton';
import { deviceCatalogInfo } from './operationsInfo';
import {
  getDeviceCatalogEntries,
  createDeviceCatalogEntry,
  updateDeviceCatalogEntry,
} from '../../services/adminService';
import { DeviceCatalogEntry, DeviceCatalogPayload, DeviceCommandPayload } from '../../types/device';
import {
  AdminCard,
  AdminCardHeader,
  AdminTable,
  AdminTableRow,
  AdminPrimaryButton,
  AdminSecondaryButton,
  AdminInlineButton,
  AdminDangerButton,
  AdminForm,
  AdminFormGroup,
  AdminFormLabel,
  AdminFormInput,
  AdminFormTextarea,
  AdminCheckbox,
  AdminCheckboxLabel,
  AdminStatusBadge,
  AdminChip,
  AdminChipList,
  AdminErrorMessage,
  AdminSuccessMessage,
  AdminLoadingMessage,
  AdminFormActions,
  AdminEmptyState,
  adminColors,
} from '../../styles/adminStyles';

// Table specific styles
const TableHeader = styled.div`
  display: grid;
  grid-template-columns: 180px 160px 1fr 100px 140px 100px;
  background: linear-gradient(135deg, rgba(0, 212, 255, 0.1) 0%, rgba(0, 164, 255, 0.05) 100%);
  font-family: 'Courier New', monospace;
  font-size: 11px;
  color: ${adminColors.secondary};
  text-transform: uppercase;
  letter-spacing: 1px;
  padding: 14px 20px;
  border-bottom: 1px solid ${adminColors.borderLight};

  @media (max-width: 1200px) {
    grid-template-columns: 140px 140px 1fr 90px 120px 90px;
    font-size: 10px;
    padding: 12px 16px;
  }
`;

const TableRow = styled(AdminTableRow)`
  grid-template-columns: 180px 160px 1fr 100px 140px 100px;

  @media (max-width: 1200px) {
    grid-template-columns: 140px 140px 1fr 90px 120px 90px;
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  flex-wrap: wrap;
  gap: 12px;
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

// Commands section in form
const CommandsSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const CommandCard = styled.div`
  background: rgba(0, 30, 60, 0.5);
  border: 1px solid ${adminColors.border};
  border-radius: 10px;
  padding: 18px;
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const CommandHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const CommandTitle = styled.span`
  font-family: 'Courier New', monospace;
  font-size: 13px;
  color: #ffffff;
  text-transform: uppercase;
  letter-spacing: 1px;
  font-weight: 600;
  text-shadow: 0 0 10px rgba(0, 212, 255, 0.5);
`;

const CommandRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const SmallFormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const SmallLabel = styled.label`
  font-family: 'Courier New', monospace;
  font-size: 11px;
  color: #ffffff;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  text-shadow: 0 0 10px rgba(255, 255, 255, 0.3);
  font-weight: 600;
`;

const SmallInput = styled.input`
  background: rgba(0, 20, 40, 0.6);
  border: 1px solid ${adminColors.border};
  border-radius: 6px;
  padding: 10px 14px;
  color: #ffffff;
  font-family: 'Courier New', monospace;
  font-size: 13px;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: ${adminColors.primary};
    box-shadow: 0 0 12px rgba(0, 212, 255, 0.25);
  }

  &::placeholder {
    color: rgba(255, 255, 255, 0.5);
  }
`;

const AddCommandButton = styled.button`
  background: linear-gradient(135deg, rgba(76, 175, 80, 0.15) 0%, rgba(76, 175, 80, 0.08) 100%);
  border: 1px solid rgba(76, 175, 80, 0.4);
  color: ${adminColors.success};
  padding: 10px 18px;
  font-family: 'Courier New', monospace;
  font-size: 12px;
  border-radius: 8px;
  cursor: pointer;
  text-transform: uppercase;
  letter-spacing: 1px;
  align-self: flex-start;
  transition: all 0.3s ease;

  &:hover {
    background: linear-gradient(135deg, rgba(76, 175, 80, 0.25) 0%, rgba(76, 175, 80, 0.15) 100%);
    box-shadow: 0 0 15px rgba(76, 175, 80, 0.2);
  }
`;

const emptyCommand: DeviceCommandPayload = {
  code: '',
  name: '',
  description: '',
};

interface FormState {
  deviceType: string;
  model: string;
  description: string;
  iconUrl: string;
  commands: DeviceCommandPayload[];
  isActive: boolean;
}

const initialFormState: FormState = {
  deviceType: '',
  model: '',
  description: '',
  iconUrl: '',
  commands: [],
  isActive: true,
};

const DeviceCatalogManager: React.FC = () => {
  const [entries, setEntries] = useState<DeviceCatalogEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [includeInactive, setIncludeInactive] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [formState, setFormState] = useState(initialFormState);
  const [editedEntry, setEditedEntry] = useState<DeviceCatalogEntry | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);

  const loadEntries = async (withInactive: boolean) => {
    setLoading(true);
    setError(null);
    try {
      const data = await getDeviceCatalogEntries(withInactive);
      setEntries(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Failed to load device catalog', err);
      setError(err instanceof Error ? err.message : 'Unable to load device catalog');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadEntries(includeInactive);
  }, [includeInactive]);

  const handleCreateClick = () => {
    setEditedEntry(null);
    setFormState(initialFormState);
    setImageFile(null);
    setImagePreview(null);
    setError(null);
    setSuccess(null);
    setModalOpen(true);
  };

  const handleEditClick = (entry: DeviceCatalogEntry) => {
    setEditedEntry(entry);
    setFormState({
      deviceType: entry.deviceType,
      model: entry.model,
      description: entry.description,
      iconUrl: entry.iconUrl || '',
      commands: entry.commands.map((cmd) => ({
        code: cmd.code,
        name: cmd.name,
        description: cmd.description,
      })),
      isActive: entry.isActive,
    });
    setImageFile(null);
    setImagePreview(entry.iconUrl || null);
    setError(null);
    setSuccess(null);
    setModalOpen(true);
  };

  const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const handleRemoveImage = () => {
    setImageFile(null);
    setImagePreview(null);
    setFormState({ ...formState, iconUrl: '' });
  };

  const handleAddCommand = () => {
    setFormState({
      ...formState,
      commands: [...formState.commands, { ...emptyCommand }],
    });
  };

  const handleRemoveCommand = (index: number) => {
    setFormState({
      ...formState,
      commands: formState.commands.filter((_, i) => i !== index),
    });
  };

  const handleCommandChange = (index: number, field: keyof DeviceCommandPayload, value: string | number | boolean) => {
    const updatedCommands = [...formState.commands];
    updatedCommands[index] = { ...updatedCommands[index], [field]: value };
    setFormState({ ...formState, commands: updatedCommands });
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    setSuccess(null);

    if (formState.commands.length === 0) {
      setError('Add at least one command.');
      return;
    }

    const invalidCommands = formState.commands.filter((cmd) => !cmd.code.trim());
    if (invalidCommands.length > 0) {
      setError('All commands must have a code.');
      return;
    }

    const payload: DeviceCatalogPayload = {
      deviceType: formState.deviceType.trim(),
      model: formState.model.trim(),
      description: formState.description.trim(),
      iconUrl: formState.iconUrl,
      commands: formState.commands.map((cmd) => ({
        ...cmd,
        code: cmd.code.trim(),
        name: cmd.name.trim(),
        description: cmd.description.trim(),
      })),
      isActive: formState.isActive,
    };

    if (!payload.deviceType || !payload.model) {
      setError('Specify device type and model.');
      return;
    }

    setSaving(true);
    try {
      if (editedEntry) {
        await updateDeviceCatalogEntry(editedEntry.id, payload, imageFile || undefined);
        setSuccess('Device updated.');
      } else {
        await createDeviceCatalogEntry(payload, imageFile || undefined);
        setSuccess('Device created.');
      }
      setModalOpen(false);
      setFormState(initialFormState);
      setEditedEntry(null);
      setImageFile(null);
      setImagePreview(null);
      await loadEntries(includeInactive);
    } catch (err) {
      console.error('Failed to save device', err);
      setError(err instanceof Error ? err.message : 'Unable to save device');
    } finally {
      setSaving(false);
    }
  };

  return (
    <AdminCard>
      <Header>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <AdminCardHeader style={{ marginBottom: 0 }}>Device Catalog</AdminCardHeader>
          <InfoButton title="Device Catalog Help" sections={deviceCatalogInfo} />
        </div>
        <Actions>
          <AdminCheckboxLabel>
            <AdminCheckbox
              type="checkbox"
              checked={includeInactive}
              onChange={(event) => setIncludeInactive(event.target.checked)}
            />
            Show archived
          </AdminCheckboxLabel>
          <AdminPrimaryButton onClick={handleCreateClick}>+ Add device</AdminPrimaryButton>
        </Actions>
      </Header>

      {loading && <AdminLoadingMessage>Loading data...</AdminLoadingMessage>}
      {error && <AdminErrorMessage>{error}</AdminErrorMessage>}

      <AdminTable>
        <TableHeader>
          <div>Device Type</div>
          <div>Model</div>
          <div>Commands</div>
          <div>Status</div>
          <div>Created</div>
          <div>Actions</div>
        </TableHeader>
        {entries.map((entry) => (
          <TableRow key={entry.id}>
            <div>{entry.deviceType}</div>
            <div>{entry.model}</div>
            <AdminChipList>
              {entry.commands.map((command) => (
                <AdminChip key={command.code}>{command.name || command.code}</AdminChip>
              ))}
            </AdminChipList>
            <div>
              <AdminStatusBadge $status={entry.isActive ? 'active' : 'inactive'}>
                {entry.isActive ? 'ACTIVE' : 'ARCHIVED'}
              </AdminStatusBadge>
            </div>
            <div style={{ fontSize: '11px', color: adminColors.textMuted }}>
              {new Date(entry.createdAt).toLocaleDateString()}
            </div>
            <div>
              <AdminInlineButton onClick={() => handleEditClick(entry)}>Edit</AdminInlineButton>
            </div>
          </TableRow>
        ))}
        {entries.length === 0 && !loading && (
          <AdminEmptyState>
            The catalog is empty. Add a device to get started.
          </AdminEmptyState>
        )}
      </AdminTable>

      <Modal
        isOpen={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setEditedEntry(null);
          setFormState(initialFormState);
          setImageFile(null);
          setImagePreview(null);
        }}
        title={editedEntry ? 'Edit Device' : 'Create Device'}
      >
        <AdminForm onSubmit={handleSubmit}>
          <AdminFormGroup>
            <AdminFormLabel>Device Type</AdminFormLabel>
            <AdminFormInput
              value={formState.deviceType}
              onChange={(event) => setFormState({ ...formState, deviceType: event.target.value })}
              placeholder="Example: Detonator"
            />
          </AdminFormGroup>

          <AdminFormGroup>
            <AdminFormLabel>Model</AdminFormLabel>
            <AdminFormInput
              value={formState.model}
              onChange={(event) => setFormState({ ...formState, model: event.target.value })}
              placeholder="Model or revision"
            />
          </AdminFormGroup>

          <AdminFormGroup>
            <AdminFormLabel>Description</AdminFormLabel>
            <AdminFormTextarea
              value={formState.description}
              onChange={(event) => setFormState({ ...formState, description: event.target.value })}
              placeholder="Short description of the device"
            />
          </AdminFormGroup>

          <AdminFormGroup>
            <AdminFormLabel>Device Image</AdminFormLabel>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {imagePreview && (
                <div style={{ position: 'relative', width: 'fit-content' }}>
                  <img
                    src={imagePreview}
                    alt="Device preview"
                    style={{
                      maxWidth: '200px',
                      maxHeight: '200px',
                      borderRadius: '8px',
                      border: `1px solid ${adminColors.border}`
                    }}
                  />
                  <button
                    type="button"
                    onClick={handleRemoveImage}
                    style={{
                      position: 'absolute',
                      top: '-8px',
                      right: '-8px',
                      background: 'rgba(244, 67, 54, 0.9)',
                      border: 'none',
                      borderRadius: '50%',
                      width: '24px',
                      height: '24px',
                      color: '#fff',
                      cursor: 'pointer',
                      fontSize: '14px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    ×
                  </button>
                </div>
              )}
              <input
                type="file"
                accept="image/*"
                onChange={handleImageChange}
                style={{
                  background: 'rgba(0, 20, 40, 0.6)',
                  border: `1px solid ${adminColors.border}`,
                  borderRadius: '6px',
                  padding: '10px',
                  color: '#fff',
                  fontFamily: "'Courier New', monospace",
                  fontSize: '13px'
                }}
              />
            </div>
          </AdminFormGroup>

          <AdminFormGroup>
            <AdminFormLabel>Commands</AdminFormLabel>
            <CommandsSection>
              {formState.commands.map((cmd, index) => (
                <CommandCard key={index}>
                  <CommandHeader>
                    <CommandTitle>Command #{index + 1}</CommandTitle>
                    <AdminDangerButton type="button" onClick={() => handleRemoveCommand(index)}>
                      Remove
                    </AdminDangerButton>
                  </CommandHeader>
                  <CommandRow>
                    <SmallFormGroup>
                      <SmallLabel>Code *</SmallLabel>
                      <SmallInput
                        value={cmd.code}
                        onChange={(e) => handleCommandChange(index, 'code', e.target.value)}
                        placeholder="BOMB_ARMED"
                      />
                    </SmallFormGroup>
                    <SmallFormGroup>
                      <SmallLabel>Name</SmallLabel>
                      <SmallInput
                        value={cmd.name}
                        onChange={(e) => handleCommandChange(index, 'name', e.target.value)}
                        placeholder="Bomb Armed"
                      />
                    </SmallFormGroup>
                  </CommandRow>
                  <SmallFormGroup>
                    <SmallLabel>Description</SmallLabel>
                    <SmallInput
                      value={cmd.description}
                      onChange={(e) => handleCommandChange(index, 'description', e.target.value)}
                      placeholder="The bomb has been armed. Defenders have 30 seconds to defuse."
                      style={{ width: '100%' }}
                    />
                  </SmallFormGroup>
                </CommandCard>
              ))}
              <AddCommandButton type="button" onClick={handleAddCommand}>
                + Add Command
              </AddCommandButton>
            </CommandsSection>
          </AdminFormGroup>

          <AdminFormGroup>
            <AdminCheckboxLabel>
              <AdminCheckbox
                type="checkbox"
                checked={formState.isActive}
                onChange={(event) => setFormState({ ...formState, isActive: event.target.checked })}
              />
              Active device
            </AdminCheckboxLabel>
          </AdminFormGroup>

          {error && <AdminErrorMessage>{error}</AdminErrorMessage>}
          {success && <AdminSuccessMessage>{success}</AdminSuccessMessage>}

          <AdminFormActions>
            <AdminSecondaryButton type="button" onClick={() => setModalOpen(false)}>
              Cancel
            </AdminSecondaryButton>
            <AdminPrimaryButton type="submit" disabled={saving}>
              {saving ? 'Saving...' : 'Save'}
            </AdminPrimaryButton>
          </AdminFormActions>
        </AdminForm>
      </Modal>
    </AdminCard>
  );
};

export default DeviceCatalogManager;
